import type { IntlKey } from 'locales';
import { TradeWay } from 'models/TradeWay';
import type { ReactNode } from 'react';
import { connect } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { dynamicQuoteTradeRequestedThunk } from 'state/dynamic/dynamicForm.thunks';
import { getQuoteType } from 'state/quotes/selectors';
import {
  canTradeBackToBack,
  getFormEnrichedData,
  getFormQuoteId,
  getFormQuoteResult,
  getSidePanelTradeWayOrDefault,
  getTradeConfirmation,
} from 'state/selectors';
import { getTradeInfosByWorkspaceTab } from 'state/selectors/common/temp';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { staticQuoteTradeRequestedThunk } from 'state/static/staticForm.thunks';
import { tradeConfirmationReset } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.action';
import type { QuoteWorkspaceType } from 'state/workspaces/model';

export interface PublicOffering {
  PublicOfferingCountry: string;
  PublicOfferingStartDate: Date;
  PublicOfferingEndDate: Date;
}

export interface OwnProps {
  tabId: string;
  workspaceType: QuoteWorkspaceType;

  className?: string;
  id: string;

  tradeLabelKey: IntlKey;
  e2e: string;

  children?: ReactNode;
}

export interface StateProps {
  quoteId: string;
  canTrade: boolean;
  tradeWay: TradeWay;
}

export interface DispatchProps {
  onCancel(quoteId: string): void;
  onTrade(quoteId: string): void;
}

export type TradeButtonProps = StateProps & DispatchProps & OwnProps;
export type TradeConfirmButtonProps = TradeButtonProps;

const mapStateToProps = (state: AppState, { tabId, workspaceType }: OwnProps): StateProps => {
  const tradeInfos = getTradeInfosByWorkspaceTab(state, workspaceType, tabId);
  const quoteId = getFormQuoteId(state, workspaceType, tabId)!;
  const tradeAuthStatus = tradeInfos.tradeAuthStatus;
  const enableTrading = tradeAuthStatus === 'Granted';
  const quoteType = getQuoteType(state, quoteId);
  const executionOnly = quoteType === 'ExecOnly' || quoteType === 'AdvancedExecOnly';
  const isExecutionOnlyAddon = getFormQuoteResult(state, workspaceType, tabId)?.isExecOnlyAddon ?? false;
  const tradeComment = tradeInfos.comment;
  const executionOnlyDealId = tradeInfos.executionOnlyDealId;
  const confirmation = getTradeConfirmation(state, workspaceType, tabId);
  const formEnrichedData = getFormEnrichedData(state, tabId);
  const publicOffering = Array.isArray(formEnrichedData.PublicOffering)
    ? (formEnrichedData.PublicOffering as PublicOffering[])
    : undefined;
  const haveFrenchPublicOffering = publicOffering?.some(po => po.PublicOfferingCountry === 'FRA') ?? false;
  const haveKidDocumentation = formEnrichedData.KidRequired as boolean;
  const hasConfirmedFrenchPublicOffering =
    !haveFrenchPublicOffering || (confirmation?.hasConfirmedFrenchPublicOffering ?? false);
  const hasConfirmedTermsheetDisclaimer = confirmation?.hasConfirmedTermsheetDisclaimer ?? false;
  const hasConfirmedKidDocumentation = !haveKidDocumentation || (confirmation?.hasConfirmedKidDocumentation ?? false);
  const canTradeExecOnlyAddon = !!tradeComment && !!executionOnlyDealId;
  const canTradeExecOnly = isExecutionOnlyAddon ? canTradeExecOnlyAddon : !!tradeComment;

  return {
    quoteId,
    canTrade:
      enableTrading && executionOnly
        ? canTradeExecOnly
        : canTradeBackToBack(state, workspaceType, tabId) &&
          hasConfirmedTermsheetDisclaimer &&
          hasConfirmedKidDocumentation &&
          hasConfirmedFrenchPublicOffering,
    tradeWay: getSidePanelTradeWayOrDefault(state, tabId),
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch, { tabId, workspaceType }: OwnProps): DispatchProps => ({
  onCancel(quoteId: string) {
    dispatch(tradeConfirmationReset(quoteId));
  },
  onTrade(quoteId: string) {
    if (workspaceType === 'static') {
      logAnalytics.trackEvent('Primary market static', 'Request for trade confirmation');
      dispatch(staticQuoteTradeRequestedThunk(tabId, quoteId));
    } else {
      logAnalytics.trackEvent('Primary market dynamic', 'Request for trade confirmation');
      dispatch(dynamicQuoteTradeRequestedThunk(tabId, quoteId));
    }
  },
});

export const connectConfirmingTradeButtons = connect(mapStateToProps, mapDispatchToProps);
